import * as T from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
export const CHUNK_SIZE=64
export function terrainCell(x:number,z:number) { return {x:Math.floor(x/CHUNK_SIZE),z:Math.floor(z/CHUNK_SIZE)} }
export function terrainKeys(x:number,z:number) {const c=terrainCell(x,z);const keys:string[]=[];for(let dz=-1;dz<=1;dz++)for(let dx=-1;dx<=1;dx++)keys.push(`${c.x+dx},${c.z+dz}`);return keys}
const mats=new Map<string,T.MeshStandardMaterial>()
function material(color:string) {if(!mats.has(color))mats.set(color,new T.MeshStandardMaterial({color,flatShading:true,roughness:1}));return mats.get(color)!}
export class Terrain {
  chunks=new Map<string,T.Group>()
  constructor(private scene:T.Scene) {this.update(0,0)}
  update(x:number,z:number) {
    const keys=new Set(terrainKeys(x,z))
    for(const [key,group] of this.chunks) if(!keys.has(key)){this.scene.remove(group);group.traverse(node=>{if(node instanceof T.Mesh)node.geometry.dispose()});this.chunks.delete(key)}
    for(const key of keys)if(!this.chunks.has(key)){const [cx,cz]=key.split(',').map(Number);const group=this.make(cx,cz);this.chunks.set(key,group);this.scene.add(group)}
  }
  private make(cx:number,cz:number) {
    let seed=(Math.imul(cx+1024,73856093)^Math.imul(cz+2048,19349663))>>>0
    const rnd=()=>{seed=(Math.imul(seed,1664525)+1013904223)>>>0;return seed/4294967296}
    const group=new T.Group();group.position.set(cx*CHUNK_SIZE,0,cz*CHUNK_SIZE)
    const geometry=new T.PlaneGeometry(CHUNK_SIZE,CHUNK_SIZE,16,16);geometry.rotateX(-Math.PI/2);geometry.translate(CHUNK_SIZE/2,0,CHUNK_SIZE/2)
    const p=geometry.attributes.position;const colors:number[]=[]
    for(let i=0;i<p.count;i++){
      const x=p.getX(i)+cx*CHUNK_SIZE,z=p.getZ(i)+cz*CHUNK_SIZE
      p.setY(i,-.09+Math.sin(x*.17)*Math.cos(z*.13)*.025)
      const mix=(Math.sin(x*.11+Math.cos(z*.08))*Math.cos(z*.12)+1)*.5
      const c=new T.Color('#a79e70').lerp(new T.Color('#c5b284'),mix);colors.push(c.r,c.g,c.b)
    }
    geometry.setAttribute('color',new T.Float32BufferAttribute(colors,3));geometry.computeVertexNormals()
    const ground=new T.Mesh(geometry,material('#ffffff'));ground.material.vertexColors=true;ground.receiveShadow=true;group.add(ground)
    const bins=new Map<string,T.BufferGeometry[]>()
    const add=(g:T.BufferGeometry,color:string,x:number,y:number,z:number,sx:number,sy:number,sz:number)=>{
      const matrix=new T.Matrix4().compose(new T.Vector3(x,y,z),new T.Quaternion().setFromAxisAngle(new T.Vector3(0,1,0),rnd()*6.28),new T.Vector3(sx,sy,sz))
      g.applyMatrix4(matrix);if(!bins.has(color))bins.set(color,[]);bins.get(color)!.push(g)
    }
    for(let i=0;i<70;i++){
      const x=rnd()*CHUNK_SIZE,z=rnd()*CHUNK_SIZE
      if(i<7){
        const h=2.5+rnd()*2.5;add(new T.CylinderGeometry(1,1,1,6),'#705c3c',x,h/2,z,.18,h,.18)
        for(let j=0;j<3;j++)add(new T.IcosahedronGeometry(1,0),j%2?'#647646':'#7e8850',x+(rnd()-.5)*1.5,h+j*.35,z+(rnd()-.5)*1.5,1.3+rnd(),.85,1.4)
      } else if(i<20){const size=.25+rnd()*.8;add(new T.IcosahedronGeometry(1,0),'#8b8265',x,size*.35,z,size,size*.65,size*.9)}
      else {for(let j=0;j<3;j++)add(new T.ConeGeometry(1,1,4),'#7d874e',x+j*.1,.2,z,.045,.4+rnd()*.3,.045)}
    }
    // Periodic, shallow streams continue across chunk boundaries in world coordinates.
    const riverPos:number[]=[];const riverIndex:number[]=[]
    for(let i=0;i<=16;i++){
      const lx=i*CHUNK_SIZE/16,wx=cx*CHUNK_SIZE+lx
      const stream=32+Math.sin(wx*.04)*12
      const localZ=((stream-cz*CHUNK_SIZE)%256+256)%256
      riverPos.push(lx,-.03,localZ-1.7,lx,-.03,localZ+1.7)
      if(i<16){const j=i*2;riverIndex.push(j,j+2,j+1,j+1,j+2,j+3)}
    }
    if(riverPos.some((v,i)=>i%3===2&&v>=0&&v<=CHUNK_SIZE)){
      const g=new T.BufferGeometry();g.setAttribute('position',new T.Float32BufferAttribute(riverPos,3));g.setIndex(riverIndex);g.computeVertexNormals();const water=new T.Mesh(g,material('#749e9a'));water.material.side=T.DoubleSide;group.add(water)
    }
    for(const [color,list] of bins){const merged=mergeGeometries(list,false);list.forEach(g=>g.dispose());if(merged){const mesh=new T.Mesh(merged,material(color));mesh.castShadow=true;mesh.receiveShadow=true;group.add(mesh)}}
    return group
  }
}
